import { getVideoPlaybackQuality } from '@streaming/mouflon-utils';

import { getPlayingTimeFraction, getTargetBuffer } from '@bufferController/BufferMetrics/helpers/GetSideMetricsAsync';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { EDebuggableModuleState, IDebbugable, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { Logger, TLogger } from '@logger/Logger';
import { ResolutionsManagerEvents } from '@resolutionsManager/ResolutionsManager.events';
import { TimeSync } from '@tymeSync/TimeSync';
import bind from '@utils/decorators/bind';

import { IBufferControllerConfig } from '../BufferController.config';
import { BufferControllerEvents } from '../BufferController.events';
import { IBufferMetrics, ITimeRange } from './IBufferMetrics';

interface IBufferedItem {
  start: number;
  end: number;
}

export class BufferMetrics extends ConfigConsumer<IBufferControllerConfig> implements IDebbugable {
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public groups: string[] = ['buffer', 'general'];
  public additionalInfo: TDebuggableAdditionalInfo = {};

  private _videoElement: HTMLVideoElement | null = null;
  private _checkIntervalId: ReturnType<typeof setInterval> | null = null;

  private _lastCheckTimestamp: number = 0;
  private _lastFramesTotal: number = 0;
  private _lastFramesDropped: number = 0;
  private _lastFramesPlayed: number = 0;
  private _lastRealFPS: number = 0;
  private _lastPlaybackFPS: number = 0;
  private _lastPlaybackPosition: number = 0;
  private _lastBufferAmount: number = 0;
  private _lastKnownLatencyZeroFrameEncodePosition: number = 0;
  private _timeSync: TimeSync | null = null;
  private _domainCache: string | null = null;

  private readonly _onLatencyExtraInformationAvailableListener: string;
  private readonly _onEntrypointURLUpdatedListener: string;

  private readonly _logger: TLogger;
  constructor(eventBus: TEventBus) {
    super('bufferController', eventBus);

    this._logger = Logger(this._eventBus, 'Doppio - BufferMetrics');
    this._onLatencyExtraInformationAvailableListener = eventBus.subscribe(
      BufferControllerEvents.latencyExtraInformationAvailable,
      this._onLatencyExtraInformationAvailable,
    );
    this._onEntrypointURLUpdatedListener = eventBus.subscribe(ResolutionsManagerEvents.entrypointURLUpdated, this._onEntrypointURLUpdated);
  }

  public destroy(): void {
    this._eventBus.unsubscribe(this._onLatencyExtraInformationAvailableListener);
    if (this._checkIntervalId) {
      globalThis.clearInterval(this._checkIntervalId);
    }

    this._eventBus.unsubscribe(this._onEntrypointURLUpdatedListener);
    this._timeSync?.destroy();
    this._timeSync = null;
  }

  public setVideoElement(videoElement: HTMLVideoElement): void {
    this._videoElement = videoElement;
    this._rearmInterval();
  }

  public removeVideoElement(): void {
    this._videoElement = null;

    if (this._checkIntervalId) {
      globalThis.clearInterval(this._checkIntervalId);
    }
  }

  @bind
  public _handleConfigUpdated(updatedValues: Partial<IBufferControllerConfig>): void {
    if (!Number.isNaN(updatedValues.checkMetricsInterval)) {
      this._rearmInterval();
    }
  }

  private _rearmInterval(): void {
    if (this._checkIntervalId) {
      globalThis.clearInterval(this._checkIntervalId);
    }

    this._checkIntervalId = globalThis.setInterval(this._performCheck, this.config.checkMetricsInterval);
  }

  private _getCurrentBufferAmount(currentRange: IBufferedItem | null): number {
    if (!this._videoElement) {
      this._logger.warn('Cannot compute buffer, there is no video element registered');
      return 0;
    }

    const { currentTime, buffered } = this._videoElement;

    if (buffered.length === 0 || currentTime > buffered.end(buffered.length - 1)) {
      return 0;
    }

    if (!currentRange) {
      return 0;
    }

    return buffered.end(buffered.length - 1) - currentTime;
  }

  private _getCurrentBufferAmountWithoutGaps(currentRange: IBufferedItem | null, bufferedTimeRanges: ITimeRange[]): number {
    if (!this._videoElement) {
      this._logger.warn('Cannot compute buffer, there is no video element registered');
      return 0;
    }

    if (!currentRange) {
      return 0;
    }

    const { currentTime } = this._videoElement;

    const timeToEndOfCurrentRange: number = currentRange.end - currentTime;
    const lastBufferedRanges: number = bufferedTimeRanges
      .filter(({ start }) => start >= currentRange.end)
      .reduce((s, c) => s + (c.end - c.start), 0);

    return timeToEndOfCurrentRange + lastBufferedRanges;
  }

  @bind
  private async _performCheck(): Promise<void> {
    if (!this._videoElement) {
      if (this._checkIntervalId) {
        globalThis.clearInterval(this._checkIntervalId);
      }

      this._logger.warn('Cannot compute metrics, there is no video element registered');
      return;
    }

    const { creationTime: msCreationTime, totalVideoFrames, droppedVideoFrames } = getVideoPlaybackQuality(this._videoElement);
    const { buffered, currentTime, paused, duration, playbackRate } = this._videoElement;

    const creationTime: number = msCreationTime / 1000;

    if (!this._lastCheckTimestamp) {
      this._lastCheckTimestamp = creationTime;
      this._lastFramesTotal = totalVideoFrames;
      this._lastFramesDropped = droppedVideoFrames;
      this._lastFramesPlayed = totalVideoFrames - droppedVideoFrames;
      this._lastPlaybackPosition = currentTime;

      return;
    }

    const currentTargetBuffer: number = await getTargetBuffer(this._eventBus);
    const playingTimeFraction: number = (await getPlayingTimeFraction(this._eventBus)) || 0;

    const playedFrames: number = totalVideoFrames - droppedVideoFrames;

    const timeDelta: number = creationTime - this._lastCheckTimestamp;
    const playbackPositionDelta: number = currentTime - this._lastPlaybackPosition;
    const totalFramesDelta: number = totalVideoFrames - this._lastFramesTotal;
    const droppedFramesDelta: number = droppedVideoFrames - this._lastFramesDropped;
    const playedFramesDelta: number = playedFrames - this._lastFramesPlayed;

    const realFPS: number = totalFramesDelta / timeDelta;
    const playbackFPS: number = totalFramesDelta / playbackPositionDelta;
    const realFPSDelta: number = this._lastRealFPS - realFPS;
    const playbackFPSDelta: number = this._lastPlaybackFPS - playbackFPS;

    const bufferedTimeRanges: ITimeRange[] = Array(buffered.length)
      .fill(null)
      .map((_, i) => {
        const timeRange: ITimeRange = {
          start: buffered.start(i),
          end: buffered.end(i),
          index: i,
          isCurrentTimeRange: false,
        };

        if ((timeRange.start <= currentTime && currentTime <= timeRange.end) || (paused && i === 0)) {
          timeRange.isCurrentTimeRange = true;
        }

        return timeRange;
      });

    const currentTimeRange: ITimeRange | null = bufferedTimeRanges.find(({ isCurrentTimeRange }) => isCurrentTimeRange) || null;

    const bufferAmount: number = this._getCurrentBufferAmount(currentTimeRange);

    const isOnBufferEdge: boolean = bufferAmount < this.config.bufferEdgeThreshold;
    const expectedTSBasedOnPosition: number = this._lastKnownLatencyZeroFrameEncodePosition
      ? this._lastKnownLatencyZeroFrameEncodePosition + currentTime * 1000
      : 0;

    const isStallCandidate: boolean =
      currentTime > 0 && !paused && (bufferAmount < this.config.bufferStallThreshold || playbackPositionDelta === 0);

    const isInLastBufferedRange: boolean = currentTimeRange?.index === bufferedTimeRanges.length - 1;

    const bufferAmountWithoutGaps: number = this._getCurrentBufferAmountWithoutGaps(currentTimeRange, bufferedTimeRanges);

    const metrics: IBufferMetrics = {
      currentTimestamp: msCreationTime,
      currentPlaybackPosition: currentTime,
      playedVideoFrames: playedFrames,
      totalVideoFramesDelta: totalFramesDelta,
      droppedVideoFramesDelta: droppedFramesDelta,
      playedVideoFramesDelta: playedFramesDelta,
      playbackSpeed: playbackRate,
      isOnBufferEdge,
      bufferAmount,
      bufferAmountWithoutGaps,
      totalVideoFrames,
      droppedVideoFrames,
      realFPS,
      playbackFPS,
      timeDelta,
      playbackPositionDelta,
      playingTimeFraction,
      realFPSDelta,
      playbackFPSDelta,
      isStallCandidate,
      bufferedTimeRanges,
      currentTimeRange,
      isInLastBufferedRange,
      knownDuration: duration,
      isPaused: paused,
      targetBuffer: currentTargetBuffer,
    };

    if (
      expectedTSBasedOnPosition &&
      this._timeSync?.currentCorrectedTime &&
      !this._videoElement.paused &&
      totalFramesDelta > 0
    ) {
      metrics.latency = this._timeSync.currentCorrectedTime - expectedTSBasedOnPosition;
    }

    this._lastCheckTimestamp = creationTime;
    this._lastFramesTotal = totalVideoFrames;
    this._lastFramesDropped = droppedVideoFrames;
    this._lastFramesPlayed = totalVideoFrames - droppedVideoFrames;
    this._lastRealFPS = realFPS;
    this._lastPlaybackFPS = playbackFPS;
    this._lastPlaybackPosition = currentTime;
    this._lastBufferAmount = bufferAmount;

    this._eventBus.publish(BufferControllerEvents.bufferMetricsAvailable, metrics);

    this.additionalInfo = metrics;
  }

  @bind
  private _onLatencyExtraInformationAvailable([pts, encodeTimestamp]: [number, number]): void {
    if (!this._timeSync && this.config.enableG2GLatencyMeasurement) {
      this._timeSync = new TimeSync(this.config.timeSyncResyncInterval);
      if (this._domainCache) {
        this._timeSync.setUrl(this._domainCache);
      }
    }
    this._lastKnownLatencyZeroFrameEncodePosition = encodeTimestamp - pts / 90;
  }

  @bind
  private _onEntrypointURLUpdated({ entrypointURL }: { entrypointURL: string }): void {
    const cdnDomain: string = new URL(entrypointURL).host.split('.').slice(-2).join('.');
    const playlistRoot: string = this.config.timeSyncUrlTemplate.replace('{{domain}}', cdnDomain);
    this._timeSync?.setUrl(playlistRoot);
    this._domainCache = playlistRoot;
    this._lastKnownLatencyZeroFrameEncodePosition = 0;
  }
}
